import type { WebStorage } from 'redux-persist/es/types';

export const PROJECT_PERSIST_BACKUP_KEY = 'persist:PROJECT:pre-migration-backup';

export type PersistProjectBackupPayload = {
  savedAtUtcMs: number;
  fromPersistVersion: number;
  state: unknown;
};

function parseBackupPayload(raw: unknown): PersistProjectBackupPayload | null {
  if (typeof raw !== 'string' || !raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PersistProjectBackupPayload>;
    if (parsed == null || typeof parsed !== 'object') return null;
    if (typeof parsed.savedAtUtcMs !== 'number' || typeof parsed.fromPersistVersion !== 'number') return null;
    if (!('state' in parsed)) return null;

    return {
      savedAtUtcMs: parsed.savedAtUtcMs,
      fromPersistVersion: parsed.fromPersistVersion,
      state: parsed.state,
    };
  } catch {
    return null;
  }
}

/** Reads the one-time pre-migration backup when present. */
export async function readPersistProjectBackup(storage: WebStorage): Promise<PersistProjectBackupPayload | null> {
  const raw = await storage.getItem(PROJECT_PERSIST_BACKUP_KEY);
  return parseBackupPayload(raw);
}

/**
 * Saves raw persisted project state once before a persist-version upgrade.
 * Does not overwrite an existing backup so the original snapshot is preserved.
 */
export async function savePersistProjectBackupIfNeeded(
  storage: WebStorage,
  fromPersistVersion: number,
  state: unknown,
  targetPersistVersion: number
): Promise<boolean> {
  if (state == null || fromPersistVersion >= targetPersistVersion) {
    return false;
  }

  const existing = await storage.getItem(PROJECT_PERSIST_BACKUP_KEY);
  if (existing != null) {
    return false;
  }

  const payload: PersistProjectBackupPayload = {
    savedAtUtcMs: Date.now(),
    fromPersistVersion,
    state,
  };

  await storage.setItem(PROJECT_PERSIST_BACKUP_KEY, JSON.stringify(payload));
  return true;
}
